// leadAssigneeWinRates.js

import { differenceInCalendarDays, startOfDay } from 'date-fns';

import { normalizeDateValue } from 'utilities/DateTime';
import { getPeopleProfiles } from 'views/utils/peopleFieldProfiles';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

export const UNASSIGNED_ASSIGNEE_KEY = '__unassigned__';

export const LEAD_ASSIGNEE_WIN_RATES_SORT = Object.freeze({
  NAME: 'name',
  ASSIGNED: 'assigned',
  PERCENT_WON: 'percentWon',
  PERCENT_LOST: 'percentLost',
  AVG_DURATION: 'avgDuration',
});

const LEAD_FIELD = Object.freeze({
  assignees: { name: 'assignees' },
  dateOfInquiry: { name: 'dateOfInquiry' },
  dateWon: { name: 'dateWon' },
  dateLost: { name: 'dateLost' },
});

/**
 * Reads a Lead schema date field into a local Date (day precision).
 *
 * @param {object|null|undefined} node
 * @param {string} fieldName
 * @returns {Date|null}
 */
export function readLeadDateField(node, fieldName) {
  const raw = resolveSchemaFieldValue({
    item: node,
    fieldDef: LEAD_FIELD[fieldName] || { name: fieldName },
    typeKey: 'LEAD',
  });

  if (!raw) return null;

  if (typeof raw === 'object' && raw.startDate != null) {
    return normalizeDateValue(raw.startDate);
  }

  return normalizeDateValue(raw);
}

/**
 * Whether inquiryDate falls inclusively in [rangeStart, rangeEnd] (day precision).
 *
 * @param {Date|null} inquiryDate
 * @param {Date|null} rangeStart
 * @param {Date|null} rangeEnd
 * @returns {boolean}
 */
export function isInquiryInRange(inquiryDate, rangeStart, rangeEnd) {
  if (!inquiryDate || !rangeStart || !rangeEnd) return false;

  const day = startOfDay(inquiryDate).getTime();
  const start = startOfDay(rangeStart).getTime();
  const end = startOfDay(rangeEnd).getTime();
  if (end < start) return false;

  return day >= start && day <= end;
}

/**
 * Resolves closed outcome from dates. If both won and lost are set, prefer the later date;
 * on a tie, prefer won.
 *
 * @param {Date|null} dateWon
 * @param {Date|null} dateLost
 * @returns {{ kind: 'won'|'lost', endDate: Date }|null}
 */
export function resolveClosedOutcome(dateWon, dateLost) {
  if (dateWon && dateLost) {
    if (dateWon.getTime() >= dateLost.getTime()) {
      return { kind: 'won', endDate: dateWon };
    }
    return { kind: 'lost', endDate: dateLost };
  }
  if (dateWon) return { kind: 'won', endDate: dateWon };
  if (dateLost) return { kind: 'lost', endDate: dateLost };
  return null;
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
function isReportableLeadNode(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.isCatalogPlaceholder === true) return false;
  return true;
}

/**
 * Builds assignee rows for the Lead win-rates report.
 *
 * @param {object} params
 * @param {object[]} params.nodes
 * @param {Date|null} params.rangeStart
 * @param {Date|null} params.rangeEnd
 * @param {string} [params.sortKey]
 * @param {'asc'|'desc'} [params.sortDirection]
 * @returns {object[]}
 */
export function buildLeadAssigneeWinRatesRows({
  nodes,
  rangeStart,
  rangeEnd,
  sortKey = LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON,
  sortDirection = 'desc',
} = {}) {
  const byAssignee = new Map();

  const ensureRow = (key, name, coverUrl = '') => {
    if (!byAssignee.has(key)) {
      byAssignee.set(key, {
        assigneeKey: key,
        assigneeName: name,
        coverUrl: coverUrl || '',
        assigned: 0,
        won: 0,
        lost: 0,
        durationDaysSum: 0,
        durationCount: 0,
      });
    }
    return byAssignee.get(key);
  };

  const list = Array.isArray(nodes) ? nodes : [];

  for (const node of list) {
    if (!isReportableLeadNode(node)) continue;

    const inquiryDate = readLeadDateField(node, 'dateOfInquiry');
    if (!isInquiryInRange(inquiryDate, rangeStart, rangeEnd)) continue;

    const dateWon = readLeadDateField(node, 'dateWon');
    const dateLost = readLeadDateField(node, 'dateLost');
    const outcome = resolveClosedOutcome(dateWon, dateLost);
    if (!outcome) continue;

    const durationDays = differenceInCalendarDays(
      startOfDay(outcome.endDate),
      startOfDay(inquiryDate),
    );

    const assigneesRaw = resolveSchemaFieldValue({
      item: node,
      fieldDef: LEAD_FIELD.assignees,
      typeKey: 'LEAD',
    });
    const profiles = getPeopleProfiles(assigneesRaw);
    if (!profiles.length) continue;

    const creditTargets = profiles.map((profile) => ({
      key: String(profile.key || '').trim(),
      name: String(profile.name || '').trim() || 'Unknown',
      coverUrl: String(profile.coverUrl || '').trim(),
    })).filter((target) => target.key && target.key !== UNASSIGNED_ASSIGNEE_KEY);

    if (!creditTargets.length) continue;

    for (const target of creditTargets) {
      const row = ensureRow(target.key, target.name, target.coverUrl);
      if (target.name && target.name !== 'Unknown' && row.assigneeName === 'Unknown') {
        row.assigneeName = target.name;
      }
      if (target.coverUrl && !row.coverUrl) {
        row.coverUrl = target.coverUrl;
      }

      row.assigned += 1;
      if (outcome.kind === 'won') row.won += 1;
      else row.lost += 1;

      if (Number.isFinite(durationDays)) {
        row.durationDaysSum += durationDays;
        row.durationCount += 1;
      }
    }
  }

  const rows = Array.from(byAssignee.values()).map((row) => {
    const assigned = row.assigned;
    const percentWon = assigned > 0 ? row.won / assigned : 0;
    const percentLost = assigned > 0 ? row.lost / assigned : 0;
    const avgDurationDays = row.durationCount > 0
      ? row.durationDaysSum / row.durationCount
      : null;

    return {
      assigneeKey: row.assigneeKey,
      assigneeName: row.assigneeName || 'Unknown',
      coverUrl: row.coverUrl || '',
      assigned,
      won: row.won,
      lost: row.lost,
      percentWon,
      percentLost,
      avgDurationDays,
    };
  });

  return sortLeadAssigneeWinRatesRows(rows, sortKey, sortDirection);
}

/**
 * @param {object[]} rows
 * @param {string} sortKey
 * @param {'asc'|'desc'} sortDirection
 * @returns {object[]}
 */
export function sortLeadAssigneeWinRatesRows(
  rows,
  sortKey = LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON,
  sortDirection = 'desc',
) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const direction = sortDirection === 'asc' ? 1 : -1;

  list.sort((left, right) => {
    let comparison = 0;

    switch (sortKey) {
      case LEAD_ASSIGNEE_WIN_RATES_SORT.NAME:
        comparison = String(left.assigneeName || '').localeCompare(
          String(right.assigneeName || ''),
          undefined,
          { sensitivity: 'base' },
        );
        break;
      case LEAD_ASSIGNEE_WIN_RATES_SORT.ASSIGNED:
        comparison = (left.assigned || 0) - (right.assigned || 0);
        break;
      case LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_LOST:
        comparison = (left.percentLost || 0) - (right.percentLost || 0);
        break;
      case LEAD_ASSIGNEE_WIN_RATES_SORT.AVG_DURATION: {
        const leftAvg = left.avgDurationDays;
        const rightAvg = right.avgDurationDays;
        if (leftAvg == null && rightAvg == null) comparison = 0;
        else if (leftAvg == null) comparison = 1;
        else if (rightAvg == null) comparison = -1;
        else comparison = leftAvg - rightAvg;
        break;
      }
      case LEAD_ASSIGNEE_WIN_RATES_SORT.PERCENT_WON:
      default:
        comparison = (left.percentWon || 0) - (right.percentWon || 0);
        break;
    }

    if (comparison !== 0) return comparison * direction;

    return String(left.assigneeName || '').localeCompare(
      String(right.assigneeName || ''),
      undefined,
      { sensitivity: 'base' },
    );
  });

  return list;
}

/**
 * Formats avg duration for display.
 *
 * @param {number|null|undefined} avgDurationDays
 * @returns {string}
 */
export function formatAvgDurationDays(avgDurationDays) {
  if (avgDurationDays == null || !Number.isFinite(avgDurationDays)) return '—';
  const rounded = Math.round(avgDurationDays);
  return `${rounded}d`;
}

/**
 * Formats a 0–1 ratio as a percent string.
 *
 * @param {number} ratio
 * @returns {string}
 */
export function formatPercent(ratio) {
  if (!Number.isFinite(ratio)) return '0%';
  return `${Math.round(ratio * 100)}%`;
}
